import React, { useMemo } from 'react';
import { InsightField } from '@/types/automationFields.ts';
import { insightFieldTableColumns } from './InsightFieldTableColumns.tsx';
import { ExtractDataStep } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { getStepErrors, StepErrorType } from '@/types/stepErrorTypes.ts';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import { makeErrorMessageString } from '../ErrorMessages.tsx';
import SortableFieldsTable, {
  ColumnDef,
} from '@/pages/Editor/components/StepSidebar/StepForms/ExtractDataStepForm/ExtractFieldsModal/SortableTable/SortableFieldsTable.tsx';

const emptyInsightField: InsightField = {
  field_id: 'DEFAULT',
  name: '',
  description: '',
  infer_answer: true,
  internet_access: false,
};

interface Props {
  step: ExtractDataStep;
}

export default function ExtractInsightsFieldsTable({ step }: Props) {
  const { fields, onChange } = useUpdateStepFields<{ insights: InsightField[] }>({ step });
  const rows = fields.insights;

  // Errors
  const fieldEmptyNameErrors = getStepErrors(step, StepErrorType.ExtractInsightEmptyName);
  const fieldEmptyDescriptionErrors = getStepErrors(
    step,
    StepErrorType.ExtractInsightEmptyDescription,
  );
  const fieldInvalidIdErrors = getStepErrors(step, StepErrorType.ExtractInsightInvalidId);

  const fieldInvalidId = useMemo(
    () => makeErrorMessageString(fieldInvalidIdErrors, step.insights),
    [fieldInvalidIdErrors, step.insights],
  );
  const fieldEmptyName = useMemo(
    () => makeErrorMessageString(fieldEmptyNameErrors, step.insights),
    [fieldEmptyNameErrors, step.insights],
  );
  const fieldEmptyDescription = useMemo(
    () => makeErrorMessageString(fieldEmptyDescriptionErrors, step.insights),
    [fieldEmptyDescriptionErrors, step.insights],
  );

  const columns: ColumnDef<InsightField>[] = insightFieldTableColumns;

  return (
    <SortableFieldsTable<InsightField>
      rows={rows}
      getId={(r) => r.field_id}
      onChange={(next) => onChange({ insights: next })}
      columns={columns}
      makeEmptyRow={() => ({ ...emptyInsightField, field_id: crypto.randomUUID().slice(0, 8) })}
      helperText={
        <>
          Use <code>Find Insights</code> to generate summaries, find themes, or extract insights.
          Best when you need a digestible version of large or complex content
        </>
      }
      addRowLabel="Add New Field"
      errors={[
        <StyledInputErrorText key="id">{fieldInvalidId}</StyledInputErrorText>,
        <StyledInputErrorText key="name">{fieldEmptyName}</StyledInputErrorText>,
        <StyledInputErrorText key="desc">{fieldEmptyDescription}</StyledInputErrorText>,
      ]}
      confirm={{
        title: 'Delete Insight Field',
        message: 'Are you sure you want to delete this field?',
        confirmText: 'Yes, delete',
      }}
    />
  );
}
